import React from 'react';

interface ModelSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedModel: 'gemini' | 'claude';
  onSelectModel: (model: 'gemini' | 'claude') => void;
}

export const ModelSelectorModal: React.FC<ModelSelectorModalProps> = ({
  isOpen,
  onClose,
  selectedModel,
  onSelectModel,
}) => {
  if (!isOpen) return null;

  const models = [
    {
      id: 'gemini' as const,
      name: 'Gemini 2.5',
      icon: '✨',
      description: 'Fast and efficient for most websites',
      features: ['Lightning fast', 'Great for landing pages', 'Imagen 3.0 images'],
      color: 'from-blue-500 to-cyan-500',
      badge: 'Recommended',
    },
    {
      id: 'claude' as const,
      name: 'Claude Sonnet 4',
      icon: '🧠',
      description: 'Advanced reasoning for complex sites',
      features: ['Deep understanding', 'Complex layouts', 'Best for detailed apps'],
      color: 'from-purple-500 to-pink-500',
      badge: 'Advanced',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Choose AI Model</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Select the best model for your project</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Model Cards */}
        <div className="p-6 space-y-4">
          {models.map((model) => {
            const isSelected = selectedModel === model.id;
            return (
              <div
                key={model.id}
                onClick={() => {
                  onSelectModel(model.id);
                  onClose();
                }}
                className={`relative cursor-pointer rounded-xl border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-transparent ring-2 ring-offset-2 ring-purple-500 dark:ring-offset-gray-800'
                    : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600'
                }`}
              >
                {/* Gradient Background */}
                {isSelected && (
                  <div className={`absolute inset-0 bg-gradient-to-r ${model.color} opacity-10 rounded-xl`}></div>
                )}

                <div className="relative p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`text-4xl`}>{model.icon}</div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{model.name}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium bg-gradient-to-r ${model.color} text-white`}>
                            {model.badge}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{model.description}</p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-2">
                    {model.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <svg className={`w-4 h-4 ${isSelected ? 'text-purple-500' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            💡 Tip: Both models support automatic fallback for optimal performance
          </p>
        </div>
      </div>
    </div>
  );
};
